import type { DriverBundleDTO, DriverOp, DriverSyncResultDTO, WholesaleRouteDTO } from '@bifeai/shared';
import { useSyncExternalStore } from 'react';
import { api, isApiError } from './api';
import { isNetworkError } from './offline';

/**
 * Celular del repartidor, también sin señal.
 *
 * Lo del día (salidas, clientes, precios de la carga) se guarda en el celular. Lo que hace el repartidor
 * (paradas, entregas, cobros, clientes nuevos, ventas en la calle) se anota en el celular con un código
 * único y se sube cuando hay señal; el servidor aplica cada código una sola vez, así que reintentar no
 * duplica nada. Mientras tanto la pantalla muestra lo del día con lo hecho encima.
 */

export type QueuedOp = DriverOp & {
  /** Ya entró en el servidor; se borra cuando baja lo del día actualizado. */
  synced?: boolean;
  /** El servidor la rechazó: el motivo, para corregir o descartar. */
  error?: string | null;
};

type Stored = { bundle: DriverBundleDTO | null; ops: QueuedOp[]; lastSync: string | null };
type State = Stored & { userId: string | null; online: boolean; syncing: boolean; loading: boolean };

const key = (userId: string) => `bifeai.driver.v1:${userId}`;

let state: State = {
  userId: null,
  bundle: null,
  ops: [],
  lastSync: null,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  loading: false,
};
const listeners = new Set<() => void>();

function set(patch: Partial<State>) {
  state = { ...state, ...patch };
  if (state.userId && ('bundle' in patch || 'ops' in patch || 'lastSync' in patch)) {
    try {
      const s: Stored = { bundle: state.bundle, ops: state.ops, lastSync: state.lastSync };
      window.localStorage.setItem(key(state.userId), JSON.stringify(s));
    } catch {
      // Sin lugar: queda en memoria hasta que se suba.
    }
  }
  listeners.forEach((l) => l());
}

function load(userId: string) {
  if (state.userId === userId) return;
  let s: Stored = { bundle: null, ops: [], lastSync: null };
  try {
    const raw = window.localStorage.getItem(key(userId));
    if (raw) s = JSON.parse(raw) as Stored;
  } catch {
    // Copia rota: se vuelve a bajar.
  }
  state = { ...state, ...s, userId };
  listeners.forEach((l) => l());
}

/** Baja lo del día. Sin señal, queda lo último guardado. */
export async function refreshBundle(): Promise<void> {
  set({ loading: true });
  try {
    const bundle = await api.get<DriverBundleDTO>('/wholesale/driver/bundle');
    set({ bundle, ops: state.ops.filter((o) => !o.synced), online: true });
  } catch (err) {
    if (isNetworkError(err)) set({ online: false });
  } finally {
    set({ loading: false });
  }
}

let running: Promise<void> | null = null;
/** Sube lo pendiente (en el orden en que se hizo) y después baja lo del día actualizado. */
export function syncOps(): Promise<void> {
  if (running) return running;
  const todo = state.ops.filter((o) => !o.synced && !o.error);
  if (!todo.length) return refreshBundle();
  running = (async () => {
    set({ syncing: true });
    try {
      for (let i = 0; i < todo.length; i += 100) {
        const chunk = todo.slice(i, i + 100);
        const res = await api.post<DriverSyncResultDTO>('/wholesale/driver/sync', {
          ops: chunk.map(({ synced: _s, error: _e, ...op }) => op),
        });
        const byId = new Map(res.results.map((r) => [r.id, r]));
        set({
          online: true,
          lastSync: new Date().toISOString(),
          ops: state.ops.map((o) => {
            const r = byId.get(o.id);
            if (!r) return o;
            return r.ok ? { ...o, synced: true, error: null } : { ...o, error: r.error ?? 'No se pudo guardar.' };
          }),
        });
      }
    } catch (err) {
      if (isNetworkError(err)) set({ online: false });
      else if (!isApiError(err, 'UNAUTHENTICATED')) console.warn('sync', err);
    } finally {
      set({ syncing: false });
      running = null;
    }
    if (state.online) await refreshBundle();
  })();
  return running;
}

/** Anota algo hecho (con o sin señal) y trata de subirlo. */
export function enqueue(op: DriverOp): void {
  set({ ops: [...state.ops, { ...op, doneAt: op.doneAt ?? new Date().toISOString() }] });
  void syncOps();
}

export function retryOp(id: string): void {
  set({ ops: state.ops.map((o) => (o.id === id ? { ...o, error: null } : o)) });
  void syncOps();
}

export function discardOp(id: string): void {
  set({ ops: state.ops.filter((o) => o.id !== id) });
}

/** Arranca el celular del repartidor: carga lo guardado, baja lo nuevo y reintenta solo. */
export function startDriver(userId: string): () => void {
  load(userId);
  const onOnline = () => {
    set({ online: true });
    void syncOps();
  };
  const onOffline = () => set({ online: false });
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  window.addEventListener('bifeai:online', onOnline);
  window.addEventListener('bifeai:offline', onOffline);
  const timer = window.setInterval(() => {
    if (state.ops.some((o) => !o.synced && !o.error)) void syncOps();
  }, 20_000);
  void syncOps();
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    window.removeEventListener('bifeai:online', onOnline);
    window.removeEventListener('bifeai:offline', onOffline);
    window.clearInterval(timer);
  };
}

export function useDriver(): State & { pending: number; failed: QueuedOp[] } {
  const s = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => state,
  );
  return { ...s, pending: s.ops.filter((o) => !o.synced && !o.error).length, failed: s.ops.filter((o) => o.error) };
}

// ---------------------------------------------------------------- lo del día con lo hecho encima
const lineTotal = (unitType: string, qty: number, price: number) => (unitType === 'WEIGHT' ? Math.round((price * qty) / 1000) : price * qty);

/** Aplica sobre lo bajado lo que todavía no está en el servidor (o que entró y no se volvió a bajar). */
export function withOps(bundle: DriverBundleDTO, ops: QueuedOp[]): DriverBundleDTO {
  const b: DriverBundleDTO = structuredClone(bundle);
  const route = (id: string): WholesaleRouteDTO | undefined => b.routes.find((r) => r.id === id);
  for (const op of ops) {
    if (op.error) continue;
    switch (op.type) {
      case 'STOP':
        for (const r of b.routes)
          for (const s of r.stops)
            if (s.id === op.stopId) {
              s.status = op.status;
              s.note = op.note ?? null;
              s.doneAt = op.status === 'PENDING' ? null : (op.doneAt ?? null);
            }
        break;
      case 'DELIVER':
        for (const r of b.routes) for (const s of r.stops) if (s.orderId === op.orderId && s.orderStatus === 'ON_ROUTE') s.orderStatus = 'DELIVERED';
        break;
      case 'PAY': {
        const c = b.customers.find((x) => x.id === op.customerId);
        if (c) c.balanceCents -= op.amountCents;
        for (const r of b.routes) {
          const mine = r.stops.some((s) => s.orderId === op.orderId) || r.spotSales.some((s) => s.orderId === op.orderId);
          for (const s of r.stops)
            if (op.orderId && s.orderId === op.orderId && s.orderDueCents !== null) s.orderDueCents = Math.max(0, s.orderDueCents - op.amountCents);
          if (mine) {
            if (op.method === 'CASH') {
              r.collected.cashCents += op.amountCents;
              r.collected.unsettledCashCents += op.amountCents;
            } else r.collected.otherCents += op.amountCents;
          }
        }
        break;
      }
      case 'NEW_CUSTOMER':
        if (!b.customers.some((c) => c.id === op.customerId))
          b.customers.push({
            id: op.customerId,
            name: op.name,
            address: op.address ?? null,
            phone: op.phone ?? null,
            balanceCents: 0,
            priceListId: b.defaultPriceListId,
          });
        break;
      case 'SPOT_SALE': {
        const r = route(op.routeId);
        if (!r || r.spotSales.some((s) => s.orderId === op.orderId)) break;
        let total = 0;
        for (const it of op.items) {
          const l = r.load.find((x) => x.productId === it.productId);
          if (!l) continue;
          l.qtySold += it.qty;
          l.qtyAvailable -= it.qty;
          total += lineTotal(l.unitType, it.qty, it.unitPriceCents);
        }
        const c = b.customers.find((x) => x.id === op.customerId);
        if (c) c.balanceCents += total;
        r.spotSales.push({ orderId: op.orderId, number: 0, customerName: c?.name ?? 'Cliente', totalCents: total });
        break;
      }
    }
  }
  return b;
}

/** Texto corto de una operación, para la lista de lo que falta subir. */
export function opLabel(op: QueuedOp, b: DriverBundleDTO | null): string {
  const cust = (id: string) => b?.customers.find((c) => c.id === id)?.name ?? 'cliente';
  switch (op.type) {
    case 'STOP':
      return op.status === 'DONE' ? 'Parada hecha' : op.status === 'SKIPPED' ? 'Parada: no se pudo' : 'Parada pendiente';
    case 'DELIVER':
      return 'Pedido entregado';
    case 'PAY':
      return `Cobro a ${cust(op.customerId)}`;
    case 'NEW_CUSTOMER':
      return `Cliente nuevo: ${op.name}`;
    case 'SPOT_SALE':
      return `Venta en la calle a ${cust(op.customerId)}`;
  }
}
